import { AuthSession } from '../../../../types';
import { authenticatedSession } from './authenticatedSession';
import { awaitingTwoFactorSession } from './awaitingTwoFactorSession';
import { unauthenticatedSession } from './unauthenticatedSession';

/**
 * Rebuilds an AuthSession from whatever is in storage. Storage holds the flat
 * token shape (`{ jwt, expiresAt, twoFactorToken? }` or nothing), which is also
 * the legacy pre-session shape, so existing persisted sessions rehydrate
 * correctly with no migration step:
 *   - nothing / malformed / no jwt  -> unauthenticated
 *   - jwt + twoFactorToken          -> authenticated
 *   - jwt only                      -> awaiting-two-factor
 *
 * Status is derived purely from token presence (the canonical rule), so it does
 * not matter whether the stored value carried a `status` field.
 */
export const deriveSessionFromStored = (stored: unknown): AuthSession => {
  try {
    if (!stored || typeof stored !== 'object') {
      return unauthenticatedSession();
    }

    const { jwt, expiresAt, twoFactorToken } = stored as Record<
      string,
      unknown
    >;

    if (typeof jwt !== 'string' || jwt.length === 0) {
      return unauthenticatedSession();
    }

    const safeExpiresAt = typeof expiresAt === 'string' ? expiresAt : '';

    if (typeof twoFactorToken === 'string' && twoFactorToken.length > 0) {
      return authenticatedSession(jwt, safeExpiresAt, twoFactorToken);
    }

    return awaitingTwoFactorSession(jwt, safeExpiresAt);
  } catch {
    return unauthenticatedSession();
  }
};
